import { Button, SvgIcon, SvgIconProps, Typography, alpha } from '@mui/material';
import React from 'react';

import { MuiSxProps } from '../types';


type SvgComponent = React.ComponentType<SvgIconProps>;

interface IActionButtonProps {
  dataTest: string;
  IconPrefix: SvgComponent;
  accessibility: {
    ariaLabel?: string;
    associatedMenuId?: string;
    id?: string;
    expanded?: boolean;
  };
  IconSuffix?: SvgComponent;
  label?: string;
  slots?: {
    button?: {
      styleProps?: MuiSxProps;
    };
    icon?: {
      styleProps?: MuiSxProps;
    };
    label?: {
      styleProps?: MuiSxProps;
    };
  };
  dataOg?: string;
  onClick(): void;
  onKeyDown?(e: React.KeyboardEvent<HTMLButtonElement>): void;
}

const ActionButtonBase: React.ForwardRefRenderFunction<HTMLButtonElement, IActionButtonProps> = (
  { IconPrefix, label, dataTest, slots = {}, accessibility, onClick, onKeyDown, dataOg, IconSuffix },
  ref
) => {
  const { button: buttonSlot, icon: iconSlot, label: labelSlot } = slots;
  const { id, expanded, associatedMenuId, ariaLabel } = accessibility;
  return (
    <Button
      data-og={dataOg}
      ref={ref}
      data-test={dataTest}
      color="primary"
      disableFocusRipple={true}
      aria-label={label ?? ariaLabel}
      aria-expanded={expanded}
      // accessibility button/menu relationship IDs
      id={id}
      aria-controls={associatedMenuId}
      sx={{
        borderColor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        '&:hover, &:focus-visible': {
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.12)
            : theme.palette.primary.light
        },
        flexShrink: 0,
        ...buttonSlot?.styleProps,
        position: 'relative',
        '&:after': {
          content: '""',
          position: 'absolute',
          top: '-5px',
          bottom: '-5px',
          left: '-5px',
          right: '-5px'
        }
      }}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <SvgIcon inheritViewBox component={IconPrefix} sx={{ fontSize: '1.5rem', ...iconSlot?.styleProps }} />
      {label && (
        <Typography
          component="span"
          variant="bodySmall"
          sx={{ lineHeight: 'unset', fontWeight: 600, ...labelSlot?.styleProps }}
        >
          {label}
        </Typography>
      )}
      {IconSuffix && <IconSuffix sx={{ fontSize: '1rem', ...iconSlot?.styleProps }} />}
    </Button>
  );
};

export const ActionButton = React.forwardRef(ActionButtonBase);
